import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@core/contexts/ProfileContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  Notification
} from '@core/api/notifications';

// ============================================================================
// Query Keys
// ============================================================================

const NOTIFICATIONS_KEY = 'notifications';
const UNREAD_COUNT_KEY = 'notifications-unread-count';

// ============================================================================
// Hook
// ============================================================================

export function useNotifications() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const userId = user?.id;

  // Fetch notifications
  const {
    data: notifications = [],
    isLoading: isLoadingNotifications,
    refetch: refetchNotifications
  } = useQuery({
    queryKey: [NOTIFICATIONS_KEY, userId],
    queryFn: () => (userId ? getNotifications(userId) : Promise.resolve([])),
    enabled: Boolean(userId),
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: true
  });

  // Fetch unread count
  const {
    data: unreadCount = 0,
    isLoading: isLoadingCount,
    refetch: refetchUnreadCount
  } = useQuery({
    queryKey: [UNREAD_COUNT_KEY, userId],
    queryFn: () => (userId ? getUnreadCount(userId) : Promise.resolve(0)),
    enabled: Boolean(userId),
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Poll every minute
    refetchOnWindowFocus: true
  });

  // Mark single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY, userId] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY, userId] });
    }
  });

  // Mark all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: () => (userId ? markAllAsRead(userId) : Promise.resolve(0)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY, userId] });
      queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY, userId] });
    }
  });

  // Refetch all notification data
  const refetchAll = () => {
    refetchNotifications();
    refetchUnreadCount();
  };

  return {
    // Data
    notifications,
    unreadCount,

    // Loading states
    isLoading: isLoadingNotifications || isLoadingCount,
    isLoadingNotifications,
    isLoadingCount,

    // Actions
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    refetch: refetchAll,

    // Mutation states
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending
  };
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Get icon emoji based on notification type
 */
export function getNotificationIcon(type: Notification['type']): string {
  switch (type) {
    case 'TRANSACTIONAL':
      return '☕';
    case 'TRIGGER':
      return '🎂';
    case 'MARKETING':
      return '🔥';
    case 'PERSONAL':
      return '💬';
    default:
      return '🔔';
  }
}

/**
 * Format notification time as relative string
 */
export function formatNotificationTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Только что';
  if (diffMins < 60) return `${diffMins} мин назад`;
  if (diffHours < 24) return `${diffHours} ч назад`;
  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';
  if (diffDays < 7) return `${diffDays} дн назад`;

  // Format as date
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short'
  });
}
